import { LoadingButton } from "@mui/lab";
import { Breadcrumbs, FormControlLabel, FormGroup, Link, Switch, Box } from "@mui/material";
import { Form } from "generated/graphql";
import { FC, ReactElement, SyntheticEvent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  fetchFormResponseErrors,
  fetchFormResponseStats,
  selectLensEditorLensId,
  selectProfileBuilderAiMode,
  selectProfileBuilderFieldById,
  selectProfileBuilderFormResponseErrors,
  selectProfileBuilderFormResponseErrorsLoading,
  setProfileBuilderAiMode,
} from "redux/slices/lensEditor";
import { P } from "theme/typography";
import ProfileIssuesDialog from "./ProfileIssuesDialog";
import RequestStatus from "redux/slices/RequestStatus";
import useAppDispatchWithSnackbar from "../../../helpers/hooks/useAppDispatchWithSnackbar";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { useNavigate } from "react-router-dom";

interface ProfileBuilderHeaderProps {
  form: Form;
}

const ProfileBuilderHeader: FC<ProfileBuilderHeaderProps> = ({ form }) => {
  const [showFormErrors, setShowFormErrors] = useState<boolean>(false);
  const [responseStatsStatus, setResponseStatsStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const validationLoading = useAppSelector(selectProfileBuilderFormResponseErrorsLoading);
  const errorsSelector = useAppSelector(selectProfileBuilderFormResponseErrors);
  const { sectionId, fieldId, cellId } = useParamNumbers(["sectionId", "fieldId", "cellId"]);
  const selectedField = useAppSelector((state) => selectProfileBuilderFieldById(state, fieldId));
  const navigate = useNavigate();
  const selectedCell = selectedField?.cells.find((c) => c.id === cellId);
  const aiMode = useAppSelector(selectProfileBuilderAiMode);
  const dispatch = useAppDispatch();

  const lensId = useAppSelector(selectLensEditorLensId);
  const snackpatch = useAppDispatchWithSnackbar();

  const [breadcrumbs, setBreadcrumbs] = useState<ReactElement[]>([]);

  useEffect(() => {
    const bcs = [`Form ID - ${form.id}`];
    if (selectedField !== undefined) {
      bcs.push(`${selectedField.name} - ${selectedField.id}`);
    }

    if (selectedCell !== undefined) {
      bcs.push(`${selectedCell.name} - ${selectedCell.id}`);
    }

    setBreadcrumbs(
      bcs.map((bc, i) => {
        const isLast = i === bcs.length - 1;
        return isLast ? (
          <P key={i}>{bc}</P>
        ) : (
          <Link key={i} underline="hover" sx={{ cursor: "pointer" }} data-index={i}>
            {bc}
          </Link>
        );
      })
    );
  }, [form, selectedField, selectedCell]);

  const handleValidateClick = (e: SyntheticEvent) => {
    e.preventDefault();
    if (lensId) {
      snackpatch(fetchFormResponseErrors({ lensId }), {
        successMessage: "Successfully retrieved profile issues",
        errorMessage: "Unable to verify profile",
      }).then(() => {
        setShowFormErrors(true);
      });
    }
  };

  const loadResponseStats = () => {
    if (lensId) {
      setResponseStatsStatus(RequestStatus.PENDING);
      snackpatch(fetchFormResponseStats({ lensId }), {
        successMessage: "Successfully retrieved profile response stats",
        errorMessage: "There was an error retrieving profile response stats",
      })
        .then(() => {
          setResponseStatsStatus(RequestStatus.SUCCEEDED);
        })
        .catch(() => {
          setResponseStatsStatus(RequestStatus.FAILED);
        });
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
      <Breadcrumbs
        onClick={(event: SyntheticEvent) => {
          if (event.target instanceof HTMLAnchorElement) {
            const index = parseInt(event.target.dataset.index!, 10);
            // navigate x levels up based on index
            if (index === 0) {
              navigate(`sections/${sectionId}`);
            } else if (index === 1) {
              navigate(`sections/${sectionId}/fields/${fieldId}`);
            }
          }
        }}>
        {breadcrumbs}
      </Breadcrumbs>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}>
        <FormGroup>
          <FormControlLabel
            control={<Switch />}
            label="AI View"
            value={aiMode}
            onChange={(_evt, checked) => dispatch(setProfileBuilderAiMode(checked))}
          />
        </FormGroup>

        <LoadingButton
          color="default"
          loading={responseStatsStatus === RequestStatus.PENDING}
          style={{
            display: responseStatsStatus !== RequestStatus.SUCCEEDED ? "inline" : "none",
          }}
          onClick={() => loadResponseStats()}>
          Load Response Stats
        </LoadingButton>

        <LoadingButton
          color="default"
          loading={validationLoading === RequestStatus.PENDING}
          onClick={errorsSelector.length > 0 ? () => setShowFormErrors(true) : handleValidateClick}>
          {errorsSelector?.length > 0 ? `View ${errorsSelector.length} Issues` : "Validate Profile"}
        </LoadingButton>
      </Box>

      <ProfileIssuesDialog open={showFormErrors} onClose={() => setShowFormErrors(false)} />
    </Box>
  );
};

export default ProfileBuilderHeader;
